import { useEffect, useState } from "react";
import { api, fmtMoney, fmtNumber, fmtDate } from "@/lib/api";
import { downloadXlsx, todayStamp } from "@/lib/xlsx";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CircleNotch, DownloadSimple, HourglassMedium, Lock } from "@phosphor-icons/react";
import { useSort, SortTh } from "@/components/sortable";

const BUCKET_LABEL = {
  "0-90": "0 a 90 días",
  "91-180": "91 a 180 días",
  "181-365": "181 a 365 días",
  ">365": "Más de 365 días",
  "sin_fecha": "Sin fecha",
};
const BUCKET_COLOR = {
  "0-90": "#065F46",
  "91-180": "#9A3412",
  "181-365": "#B45309",
  ">365": "#991B1B",
  "sin_fecha": "#64748B",
};

export default function InventarioAnalisis() {
  const [aging, setAging] = useState(null);
  const [capital, setCapital] = useState(null);

  useEffect(() => {
    api.get("/inventario/aging").then((r) => setAging(r.data));
    api.get("/inventario/capital-inmovilizado").then((r) => setCapital(r.data));
  }, []);

  const agingSort = useSort(aging?.rows || []);
  const capitalSort = useSort(capital?.rows || []);

  const exportAging = () => {
    const rows = [["SKU", "Producto", "Lote", "Cantidad", "Valor inventario", "Días en bodega", "Antigüedad"]];
    (aging?.rows || []).forEach((r) =>
      rows.push([r.sku, r.producto, r.numero_lote, r.cantidad, r.valor_inventario, r.dias_en_bodega ?? "", BUCKET_LABEL[r.bucket]]));
    downloadXlsx(`aging_inventario_${todayStamp()}.xlsx`, [
      { name: "Aging", rows, cols: [{ wch: 14 }, { wch: 26 }, { wch: 16 }, { wch: 12 }, { wch: 16 }, { wch: 14 }, { wch: 18 }] },
    ]);
  };

  const exportCapital = () => {
    const rows = [["SKU", "Producto", "Stock actual", "Valor inventario", "Días sin movimiento", "Última salida", "Inmovilizado"]];
    (capital?.rows || []).forEach((r) =>
      rows.push([r.sku, r.producto, r.stock_actual, r.valor_inventario, r.dias_sin_movimiento ?? "", r.ultima_salida ? r.ultima_salida.slice(0, 10) : "Nunca", r.inmovilizado ? "Sí" : "No"]));
    downloadXlsx(`capital_inmovilizado_${todayStamp()}.xlsx`, [
      { name: "Capital", rows, cols: [{ wch: 14 }, { wch: 26 }, { wch: 12 }, { wch: 16 }, { wch: 18 }, { wch: 14 }, { wch: 12 }] },
    ]);
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="inventario-analisis-page">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight text-[#0F172A]">Análisis de inventario</h1>
        <p className="text-[#475569] mt-1">Antigüedad del stock (aging) y capital inmovilizado, valorizado a CMP.</p>
      </div>

      <Tabs defaultValue="aging">
        <TabsList className="bg-[#F1F5F9]">
          <TabsTrigger value="aging" data-testid="tab-aging">Inventario envejecido</TabsTrigger>
          <TabsTrigger value="capital" data-testid="tab-capital">Capital inmovilizado</TabsTrigger>
        </TabsList>

        {/* AGING */}
        <TabsContent value="aging" className="space-y-4 mt-4">
          {!aging ? (
            <Loader />
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                {["0-90", "91-180", "181-365", ">365"].map((b) => (
                  <div key={b} className="bg-white border border-[#E2E8F0] rounded-md p-4" data-testid={`aging-bucket-${b}`}>
                    <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: BUCKET_COLOR[b] }}>{BUCKET_LABEL[b]}</p>
                    <p className="text-xl font-black text-[#0F172A] font-heading mt-1">{fmtMoney(aging.totales[b].valor)}</p>
                    <p className="text-xs text-[#64748B]">{fmtNumber(aging.totales[b].cantidad)} u</p>
                  </div>
                ))}
                <div className="bg-[#0F172A] rounded-md p-4 text-white">
                  <p className="text-xs font-semibold uppercase tracking-wider text-white/70">Total</p>
                  <p className="text-xl font-black font-heading mt-1">{fmtMoney(aging.valor_total)}</p>
                </div>
              </div>

              <div className="bg-white border border-[#E2E8F0] rounded-md overflow-hidden">
                <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <HourglassMedium size={20} weight="bold" className="text-[#9A3412]" />
                    <h2 className="font-heading text-lg font-bold text-[#0F172A]">Detalle por lote</h2>
                  </div>
                  <Button data-testid="export-aging" variant="outline" onClick={exportAging} disabled={!aging.rows.length} className="h-10 border-[#E2E8F0] font-semibold">
                    <DownloadSimple size={18} weight="bold" className="mr-1.5" /> Excel
                  </Button>
                </div>
                {aging.rows.length === 0 ? (
                  <p className="px-6 py-12 text-center text-[#64748B]">Sin stock en bodega.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="aging-table">
                      <thead>
                        <tr className="text-left text-xs uppercase tracking-wider text-[#64748B] bg-[#F8F9FA]">
                          <SortTh label="SKU" sortKey="sku" activeKey={agingSort.sortKey} dir={agingSort.sortDir} onSort={agingSort.toggleSort} className="!px-6" />
                          <SortTh label="Producto" sortKey="producto" activeKey={agingSort.sortKey} dir={agingSort.sortDir} onSort={agingSort.toggleSort} className="!px-6" />
                          <SortTh label="Lote" sortKey="numero_lote" activeKey={agingSort.sortKey} dir={agingSort.sortDir} onSort={agingSort.toggleSort} className="!px-6" />
                          <SortTh label="Cantidad" sortKey="cantidad" activeKey={agingSort.sortKey} dir={agingSort.sortDir} onSort={agingSort.toggleSort} align="right" className="!px-6" />
                          <SortTh label="Valor" sortKey="valor_inventario" activeKey={agingSort.sortKey} dir={agingSort.sortDir} onSort={agingSort.toggleSort} align="right" className="!px-6" />
                          <SortTh label="Días" sortKey="dias_en_bodega" activeKey={agingSort.sortKey} dir={agingSort.sortDir} onSort={agingSort.toggleSort} align="right" className="!px-6" />
                          <SortTh label="Antigüedad" sortKey="bucket" activeKey={agingSort.sortKey} dir={agingSort.sortDir} onSort={agingSort.toggleSort} align="right" className="!px-6" />
                        </tr>
                      </thead>
                      <tbody>
                        {agingSort.sorted.map((r, i) => (
                          <tr key={i} className="border-t border-[#E2E8F0] hover:bg-[#F8F9FA] transition-colors">
                            <td className="px-6 py-3 font-semibold text-[#0F172A]">{r.sku}</td>
                            <td className="px-6 py-3 text-[#475569]">{r.producto}</td>
                            <td className="px-6 py-3 text-[#64748B]">{r.numero_lote}</td>
                            <td className="px-6 py-3 text-right">{fmtNumber(r.cantidad)}</td>
                            <td className="px-6 py-3 text-right font-semibold text-[#0F172A]">{fmtMoney(r.valor_inventario)}</td>
                            <td className="px-6 py-3 text-right text-[#475569]">{r.dias_en_bodega ?? "—"}</td>
                            <td className="px-6 py-3 text-right">
                              <span className="text-xs font-bold" style={{ color: BUCKET_COLOR[r.bucket] }}>{BUCKET_LABEL[r.bucket]}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </TabsContent>

        {/* CAPITAL */}
        <TabsContent value="capital" className="space-y-4 mt-4">
          {!capital ? (
            <Loader />
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                <div className="bg-white border border-[#E2E8F0] rounded-md p-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#64748B]">Valor total</p>
                  <p className="text-2xl font-black text-[#0F172A] font-heading mt-1">{fmtMoney(capital.valor_total)}</p>
                </div>
                <div className="bg-white border border-[#FECACA] rounded-md p-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#991B1B] flex items-center gap-1"><Lock size={13} weight="bold" /> Capital inmovilizado</p>
                  <p className="text-2xl font-black text-[#991B1B] font-heading mt-1" data-testid="capital-total">{fmtMoney(capital.capital_inmovilizado)}</p>
                </div>
                <div className="bg-white border border-[#E2E8F0] rounded-md p-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#64748B]">Umbral sin movimiento</p>
                  <p className="text-2xl font-black text-[#0F172A] font-heading mt-1">{capital.umbral_dias} días</p>
                </div>
              </div>

              <div className="bg-white border border-[#E2E8F0] rounded-md overflow-hidden">
                <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between">
                  <h2 className="font-heading text-lg font-bold text-[#0F172A]">Productos por capital (mayor primero)</h2>
                  <Button data-testid="export-capital" variant="outline" onClick={exportCapital} disabled={!capital.rows.length} className="h-10 border-[#E2E8F0] font-semibold">
                    <DownloadSimple size={18} weight="bold" className="mr-1.5" /> Excel
                  </Button>
                </div>
                {capital.rows.length === 0 ? (
                  <p className="px-6 py-12 text-center text-[#64748B]">Sin stock en bodega.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="capital-table">
                      <thead>
                        <tr className="text-left text-xs uppercase tracking-wider text-[#64748B] bg-[#F8F9FA]">
                          <SortTh label="SKU" sortKey="sku" activeKey={capitalSort.sortKey} dir={capitalSort.sortDir} onSort={capitalSort.toggleSort} className="!px-6" />
                          <SortTh label="Producto" sortKey="producto" activeKey={capitalSort.sortKey} dir={capitalSort.sortDir} onSort={capitalSort.toggleSort} className="!px-6" />
                          <SortTh label="Stock" sortKey="stock_actual" activeKey={capitalSort.sortKey} dir={capitalSort.sortDir} onSort={capitalSort.toggleSort} align="right" className="!px-6" />
                          <SortTh label="Valor" sortKey="valor_inventario" activeKey={capitalSort.sortKey} dir={capitalSort.sortDir} onSort={capitalSort.toggleSort} align="right" className="!px-6" />
                          <SortTh label="Días sin mov." sortKey="dias_sin_movimiento" activeKey={capitalSort.sortKey} dir={capitalSort.sortDir} onSort={capitalSort.toggleSort} align="right" className="!px-6" />
                          <SortTh label="Última salida" sortKey="ultima_salida" activeKey={capitalSort.sortKey} dir={capitalSort.sortDir} onSort={capitalSort.toggleSort} align="right" className="!px-6" />
                        </tr>
                      </thead>
                      <tbody>
                        {capitalSort.sorted.map((r, i) => (
                          <tr key={i} className={`border-t border-[#E2E8F0] transition-colors ${r.inmovilizado ? "bg-[#FEF2F2]/40 hover:bg-[#FEF2F2]" : "hover:bg-[#F8F9FA]"}`}>
                            <td className="px-6 py-3 font-semibold text-[#0F172A]">{r.sku}</td>
                            <td className="px-6 py-3 text-[#475569]">{r.producto}</td>
                            <td className="px-6 py-3 text-right">{fmtNumber(r.stock_actual)}</td>
                            <td className="px-6 py-3 text-right font-semibold text-[#0F172A]">{fmtMoney(r.valor_inventario)}</td>
                            <td className="px-6 py-3 text-right text-[#475569]">{r.dias_sin_movimiento ?? "Sin salidas"}</td>
                            <td className="px-6 py-3 text-right text-[#64748B]">{r.ultima_salida ? fmtDate(r.ultima_salida) : "Nunca"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Loader() {
  return (
    <div className="flex items-center justify-center py-20">
      <CircleNotch size={28} className="animate-spin text-[#0F172A]" />
    </div>
  );
}
